import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  position: relative;
  margin: 24px 0;
`;

const Pre = styled.pre`
  background: ${(props) =>
    props.theme.name === 'dark' ? '#0F172A' : '#F8FAFC'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
  border-radius: 8px;
  padding: 20px;
  overflow-x: auto;
  font-family: 'SF Mono', Monaco, Consolas, 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.6;
  color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#334155')};
  margin: 0;
`;

const CopyButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 6px 12px;
  background: ${(props) =>
    props.copied
      ? props.theme.name === 'dark'
        ? 'rgba(34, 197, 94, 0.15)'
        : 'rgba(34, 197, 94, 0.1)'
      : props.theme.name === 'dark'
      ? 'rgba(255,255,255,0.05)'
      : 'rgba(0,0,0,0.05)'};
  border: 1px solid
    ${(props) =>
      props.copied
        ? '#22C55E'
        : props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgba(0,0,0,0.1)'};
  border-radius: 6px;
  color: ${(props) =>
    props.copied
      ? '#22C55E'
      : props.theme.name === 'dark'
      ? '#E2E8F0'
      : '#64748B'};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: ${(props) =>
      props.copied
        ? props.theme.name === 'dark'
          ? 'rgba(34, 197, 94, 0.2)'
          : 'rgba(34, 197, 94, 0.15)'
        : props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgba(0,0,0,0.08)'};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const CodeBlock = ({ children, language = 'jsx', showCopy = true }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Container>
      {showCopy && (
        <CopyButton onClick={handleCopy} copied={copied}>
          {copied ? (
            <>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              Copied!
            </>
          ) : (
            <>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              Copy
            </>
          )}
        </CopyButton>
      )}
      <Pre>
        <code>{children}</code>
      </Pre>
    </Container>
  );
};

CodeBlock.propTypes = {
  children: PropTypes.string.isRequired,
  language: PropTypes.string,
  showCopy: PropTypes.bool,
};

export default CodeBlock;